import type { CrossPlatformPrefill } from '@app/core/models';

/* ------------------------------------------------------------------ */
/*  CW-13h LD-2 — closes backend SA LOW-2.                              */
/*                                                                     */
/*  Type-guards the `prefill` block returned by verify-2fa Branch B    */
/*  BEFORE it is allowed to populate any form field or state signal.   */
/*  If any required field is absent, non-string, empty, or malformed   */
/*  (e.g. invalid email regex, non-digit phone), an Err result is      */
/*  returned and the caller surfaces an error on the login page —      */
/*  the wizard does NOT open.                                          */
/*                                                                     */
/*  IMPORTANT: never use `?? ''` defaults here — an absent or empty   */
/*  field must propagate as Err, not silently succeed with an empty   */
/*  prefill value.                                                     */
/* ------------------------------------------------------------------ */

export interface ValidationError {
  readonly field: string;
  readonly reason: string;
}

export type Result<T, E> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

/** Whitelist of expected keys — defensive against prototype pollution. */
const EXPECTED_KEYS = ['email', 'userName', 'phoneNumber', 'countryCode'] as const;

/** Simple email regex — matches `local@domain.tld`. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Phone and country code must be digit-only strings. */
const DIGITS_ONLY = /^\d+$/;

/**
 * Validate the Branch B `prefill` payload. Returns a typed
 * `CrossPlatformPrefill` on success or a structured Err on the FIRST
 * failing field. Caller logs the malformed shape (without leaking PII)
 * and shows a generic banner.
 */
export function validateCrossPlatformPrefill(
  raw: unknown,
): Result<CrossPlatformPrefill, ValidationError> {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: { field: 'prefill', reason: 'not_an_object' } };
  }

  // Reject prototype-pollution payloads (`__proto__`, `constructor`, etc.).
  // The shape we accept is a plain Record with only the 4 expected keys —
  // any extra key, including own-enumerable `__proto__`, is rejected.
  const ownKeys = Object.keys(raw as Record<string, unknown>);
  for (const k of ownKeys) {
    if (!EXPECTED_KEYS.includes(k as (typeof EXPECTED_KEYS)[number])) {
      return { ok: false, error: { field: k, reason: 'unexpected_key' } };
    }
  }

  const obj = raw as Record<string, unknown>;

  // email
  const email = obj['email'];
  if (typeof email !== 'string' || email.length === 0) {
    return { ok: false, error: { field: 'email', reason: 'missing_or_empty' } };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { ok: false, error: { field: 'email', reason: 'invalid_format' } };
  }

  // userName
  const userName = obj['userName'];
  if (typeof userName !== 'string' || userName.length === 0) {
    return { ok: false, error: { field: 'userName', reason: 'missing_or_empty' } };
  }

  // phoneNumber — digits-only
  const phoneNumber = obj['phoneNumber'];
  if (typeof phoneNumber !== 'string' || phoneNumber.length === 0) {
    return { ok: false, error: { field: 'phoneNumber', reason: 'missing_or_empty' } };
  }
  if (!DIGITS_ONLY.test(phoneNumber)) {
    return { ok: false, error: { field: 'phoneNumber', reason: 'non_digit' } };
  }

  // countryCode — digits-only
  const countryCode = obj['countryCode'];
  if (typeof countryCode !== 'string' || countryCode.length === 0) {
    return { ok: false, error: { field: 'countryCode', reason: 'missing_or_empty' } };
  }
  if (!DIGITS_ONLY.test(countryCode)) {
    return { ok: false, error: { field: 'countryCode', reason: 'non_digit' } };
  }

  // Split userName at LAST space — preserves multi-word first names
  // (e.g. "Mary Jane Smith" → firstName "Mary Jane", lastName "Smith").
  // No space → all to firstName, lastName empty.
  const trimmed = userName.trim();
  const lastSpace = trimmed.lastIndexOf(' ');
  const firstName = lastSpace >= 0 ? trimmed.slice(0, lastSpace).trim() : trimmed;
  const lastName = lastSpace >= 0 ? trimmed.slice(lastSpace + 1).trim() : '';

  return {
    ok: true,
    value: {
      email,
      userName: trimmed,
      firstName,
      lastName,
      phoneNumber,
      countryCode,
    },
  };
}
